/**
 * The microphone half of the visit recorder in a browser or the desktop app:
 * MediaRecorder (Opus in WebM), one recorder per part. A browser tab is not a
 * reliable recorder once it is hidden or the screen locks, so this never
 * claims background support and the panel says to keep the window open.
 */
import debug from '../utilities/debug';

export interface ClosedPart { uri: string; mime: string; ext: string }

let stream: MediaStream | null = null;
let recorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let analyser: AnalyserNode | null = null;
let audioContext: AudioContext | null = null;
const samples = new Uint8Array(256);

const mimeType = () => {
  const preferred = 'audio/webm;codecs=opus';
  return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(preferred) ? preferred : 'audio/webm';
};

async function ensureStream(isCurrent: () => boolean = () => true): Promise<MediaStream | null> {
  if (!isCurrent()) return null;
  if (stream && stream.getAudioTracks().some((t) => t.readyState === 'live')) return stream;
  const acquired = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
  if (!isCurrent()) {
    acquired.getTracks().forEach((track) => track.stop());
    return null;
  }
  stream = acquired;
  try {
    const Ctx: typeof AudioContext | undefined = (globalThis as any).AudioContext || (globalThis as any).webkitAudioContext;
    if (Ctx) {
      audioContext = new Ctx();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      audioContext.createMediaStreamSource(stream).connect(analyser);
    }
  } catch { /* the waveform is decoration */ }
  return stream;
}

export const engine = {
  persistsParts: false,
  supported(): boolean {
    return typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && !!navigator?.mediaDevices?.getUserMedia;
  },
  async requestPermission(isCurrent: () => boolean = () => true): Promise<boolean> {
    try { return !!(await ensureStream(isCurrent)); } catch { return false; }
  },
  async freeBytes(): Promise<number> {
    return Number.MAX_SAFE_INTEGER;
  },
  backgroundSupported(): boolean | null {
    return false;
  },
  resetBackgroundProbe(): void { /* a browser tab never records in the background */ },
  async startPart(): Promise<void> {
    const s = await ensureStream();
    if (!s) throw new Error('Recording start was cancelled');
    chunks = [];
    recorder = new MediaRecorder(s, { mimeType: mimeType(), audioBitsPerSecond: 32000 });
    recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    recorder.start(1000);
  },
  pause(): void {
    try { if (recorder?.state === 'recording') recorder.pause(); } catch { /* */ }
  },
  async resume(): Promise<void> {
    if (!recorder) { await engine.startPart(); return; }
    if (recorder.state === 'paused') recorder.resume();
  },
  isRecording(): boolean {
    return recorder?.state !== 'inactive';
  },
  level(): number {
    if (!analyser) return 0;
    analyser.getByteTimeDomainData(samples);
    let peak = 0;
    for (const v of samples) peak = Math.max(peak, Math.abs(v - 128));
    return Math.min(1, peak / 64);
  },
  async closePart(_dir: string, _index: number): Promise<ClosedPart | null> {
    const r = recorder;
    recorder = null;
    if (!r) return null;
    await new Promise<void>((resolve) => {
      r.onstop = () => resolve();
      try { r.stop(); } catch (err: any) { debug.warn('[visitRecorder] web stop failed', { error: err?.message }); resolve(); }
    });
    if (!chunks.length) return null;
    const blob = new Blob(chunks, { type: 'audio/webm' });
    chunks = [];
    return { uri: URL.createObjectURL(blob), mime: 'audio/webm', ext: 'webm' };
  },
  async release(): Promise<void> {
    try { recorder?.stop(); } catch { /* */ }
    recorder = null;
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    analyser = null;
    try { await audioContext?.close(); } catch { /* */ }
    audioContext = null;
  },
  partsDir(_recordingId: string): string {
    return '';
  },
  async deleteFile(uri: string): Promise<void> {
    try { if (uri.startsWith('blob:')) URL.revokeObjectURL(uri); } catch { /* */ }
  },
};
